import React, {useState} from "react";
import {
    Box,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListSubheader,
    Button,
    FormControlLabel,
    Checkbox,
    Popover,
    TextField,
    Divider,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import BasicDatePicker from "./Charts/DatePickerModule";
import YAxisSelect from "./Charts/YAxisSelect";
import UserSelectDropdown from "./Charts/UserSelectDropdown";
import CSVDownloadButton from "./CSVDownloadButton";
import {userIDS} from "./user";
import {useCoachClients} from "./utils/useCoachClients";
import NotificationPopover from "./utils/NotificationPopover";

const CustomMenu = ({
                        data,
                        loadAllDays,
                        setLoadAllDays,
                        startDate,
                        setStartDate,
                        endDate,
                        setEndDate,
                        setYAxisMax,
                        isAdmin,
                        userRole,
                        selectedUserName,
                        handleUserSelect,
                        user,
                        selectedUserId,
                        handleToggleView,
                        showListView,
                        setShowMapOverlay,
                        showMapOverlay,
                        authorizedHttpClient,
                        drawerWidth = 320, // Default width (can be overridden)
                    }) => {
    // State for mobile drawer open/close
    const [mobileOpen, setMobileOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // Toggle mobile drawer state
    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

// Notification state:
    const [notificationAnchor, setNotificationAnchor] = useState(null);
    const handleOpenNotificationPopover = (e) => setNotificationAnchor(e.currentTarget);
    const handleCloseNotificationPopover = () => setNotificationAnchor(null);


    // Fetch dropdown data (for coaches & admins)
    const fetchedCoachClients = useCoachClients(authorizedHttpClient);
    const shouldShowDropdown = isAdmin || userRole === "COACH";
    const dropdownData = isAdmin
        ? userIDS
        : userRole === "COACH"
            ? fetchedCoachClients
            : [];

    // Group 1: Date & Axis Settings
    const dateAxisSection = (
        <List
            subheader={
                <ListSubheader component="div" sx={{fontSize: 14, lineHeight: "24px"}}>
                    Zeitspanne
                </ListSubheader>
            }
        >
            <ListItem sx={{py: 1}}>
                <BasicDatePicker label="Anfangsdatum" value={startDate} setValue={setStartDate}/>
            </ListItem>
            <ListItem sx={{py: 1}}>
                <BasicDatePicker label="Enddatum" value={endDate} setValue={setEndDate}/>
            </ListItem>
            <ListItem sx={{py: 1}}>
                <YAxisSelect setYAxisMax={setYAxisMax}/>
            </ListItem>
        </List>
    );

    // Group 2: User Filters (if applicable)
    const userFilterSection = shouldShowDropdown && (
        <List
            subheader={
                <ListSubheader component="div" sx={{fontSize: 14, lineHeight: "24px"}}>
                    Nutzer auswählen
                </ListSubheader>
            }
        >

            <UserSelectDropdown
                userIDS={dropdownData}
                selectedUserName={selectedUserName}
                onUserSelect={handleUserSelect}
                user={user}
            />

        </List>
    );

    // Group 3: View Options
    const viewToggleSection = (
        <List
            subheader={
                <ListSubheader component="div" sx={{fontSize: 14, lineHeight: "24px"}}>
                    Ansichten
                </ListSubheader>
            }
        >
            <ListItem sx={{p: 1}}>
                <Button variant="contained" onClick={handleToggleView} fullWidth>
                    {showListView ? "Listenansicht" : "Tagesansicht"}
                </Button>
            </ListItem>
            {isAdmin && (
                <ListItem sx={{p: 1}}>
                    <Button
                        variant="contained"
                        onClick={() => setShowMapOverlay(!showMapOverlay)}
                        fullWidth
                    >
                        {showMapOverlay ? "Hide Map" : "Show Map"}
                    </Button>
                </ListItem>
            )}
            {showListView && isAdmin && (
                <ListItem sx={{py: 1}}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={loadAllDays}
                                onChange={(e) => setLoadAllDays(e.target.checked)}
                            />
                        }
                        label="auch Tage ohne Mahlzeiten"
                    />
                </ListItem>
            )}
        </List>
    );

    // Group 4: Notifications (Popover)
    const notificationsSection = (
        <List
            subheader={
                <ListSubheader component="div" sx={{ fontSize: 14, lineHeight: "24px" }}>
                    Nachrichten
                </ListSubheader>
            }
        >
            <ListItem sx={{ py: 1 }}>
                <Button fullWidth onClick={handleOpenNotificationPopover}>
                    Nachricht an Nutzer
                </Button>
                <NotificationPopover
                    open={Boolean(notificationAnchor)}
                    anchorEl={notificationAnchor}
                    onClose={handleCloseNotificationPopover}
                    selectedUserId={selectedUserId}
                />
            </ListItem>
        </List>
    );

    // Group 5: Data Export & Start Analysis
    const exportSection = (
        <List
            subheader={
                <ListSubheader component="div" sx={{fontSize: 14, lineHeight: "24px"}}>
                    Data Export
                </ListSubheader>
            }
        >
            <ListItem sx={{py: 1, justifyContent: "center"}}>
                <CSVDownloadButton
                    rawData={data}
                    startDate={startDate}
                    endDate={endDate}
                    filename="GlucoseAnalysis.csv"
                    user={user}
                    selectedUserId={selectedUserId}
                    isAdmin={isAdmin}
                />
            </ListItem>

        </List>
    );

    // Build the complete drawer content
    const drawerContent = (
        <Box
            sx={{
                width: drawerWidth,
                pt: 4,
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            {dateAxisSection}
            <Divider sx={{my: 1}}/>
            {userFilterSection}
            {userFilterSection && <Divider sx={{my: 1}}/>}
            {viewToggleSection}
            <Divider sx={{my: 1}}/>

            {isAdmin && notificationsSection}
            <Divider sx={{my: 1}}/>
            {exportSection}
        </Box>
    );

    return (
        <>
            {/* Mobile: Hamburger icon */}
            {isMobile && (
                <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    onClick={handleDrawerToggle}
                    sx={{m: 2}}
                >
                    <MenuIcon/>
                </IconButton>
            )}

            {/* Mobile Drawer (Overlay) */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{keepMounted: true}}
                sx={{
                    display: {xs: "block", md: "none"},
                    "& .MuiDrawer-paper": {boxSizing: "border-box", width: drawerWidth},
                }}
            >
                {drawerContent}
            </Drawer>

            {/* Desktop Drawer (Permanent Sidebar) */}
            <Drawer
                variant="permanent"
                sx={{
                    display: {xs: "none", md: "block"},
                    "& .MuiDrawer-paper": {
                        boxSizing: "border-box",
                        width: drawerWidth,

                    },
                }}
                open
            >
                {drawerContent}
            </Drawer>
        </>
    );
};

export default CustomMenu;
